import { Box } from '@mui/material';
import { IconButton, Tooltip, colors } from '@sweep-io/sweep-design';
import { Like, Dislike } from '@sweep-io/sweep-design/dist/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useAiAgentApiFacade } from '../../../../apis/facades/useAiAgentApiFacade';
import { telemetry } from '../../../../telemetry';
import { selectActiveAgentId, setMessageFeedback } from '../../../aiAgentCenter/agentsChatsReducer';
import { usePermissions } from '../../../../permissions/usePermissions';

type FeedbackType = 'like' | 'dislike';

interface MessageFeedbackButtonsProps {
  chatId: string;
  runId: string;
  currentFeedback?: FeedbackType;
  crmOrgId: string;
}

export const MessageFeedbackButtons = ({
  chatId,
  runId,
  currentFeedback,
  crmOrgId,
}: MessageFeedbackButtonsProps) => {
  const dispatch = useDispatch();
  const agentId = useSelector(selectActiveAgentId);
  const { post_messageFeedback } = useAiAgentApiFacade();
  const { evaluatePermissions } = usePermissions();
  const [isAllowedFeedback] = evaluatePermissions([{ permission: ['create:ai-agent:feedback'], crmOrgId }]);

  const handleFeedback = (feedback: FeedbackType) => {
    if (!agentId) return;

    // Optimistic update: update Redux immediately, fire-and-forget API call.
    // No rollback on API failure - feedback is non-critical UX enhancement.
    dispatch(setMessageFeedback({ agentId, chatId, runId, feedback }));

    const satisfaction = feedback === 'like' ? 1 : 0;
    post_messageFeedback(chatId, runId, satisfaction).catch((error) => {
      telemetry.captureError(new Error('Failed to submit message feedback'), error);
    });
  };

  const handleClearFeedback = () => {
    if (!agentId) return;

    // Clear feedback locally only - no API call (this is the product)
    dispatch(setMessageFeedback({ agentId, chatId, runId, feedback: undefined }));
  };

  const isLiked = currentFeedback === 'like';
  const isDisliked = currentFeedback === 'dislike';

  const activeButtonSx = {
    '& button': {
      backgroundColor: colors.grey[120],
      opacity: 0.7,
      'svg path': {
        stroke: colors.grey[120],
        fill: '#696D6F',
      },
      '&:hover': {
        opacity: 1,
      },
    },
  };

  if (isLiked) {
    return (
      <Tooltip title="Undo" placement="top">
        <Box sx={activeButtonSx}>
          <IconButton onClick={handleClearFeedback} size="tiny" variant="flat">
            <Like color={colors.grey[700]} />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  if (isDisliked) {
    return (
      <Tooltip title="Undo" placement="top">
        <Box sx={activeButtonSx}>
          <IconButton onClick={handleClearFeedback} size="tiny" variant="flat">
            <Dislike color={colors.grey[700]} />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  const disabledTitle = `To provide feedback, please contact your admin`;

  return (
    <>
      <Tooltip title={isAllowedFeedback ? 'Like' : disabledTitle} placement="top">
        <span>
          <IconButton
            onClick={() => handleFeedback('like')}
            size="tiny"
            variant="flat"
            disabled={!isAllowedFeedback}
          >
            <Like />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={isAllowedFeedback ? 'Dislike' : disabledTitle} placement="top">
        <span>
          <IconButton
            onClick={() => handleFeedback('dislike')}
            size="tiny"
            variant="flat"
            disabled={!isAllowedFeedback}
          >
            <Dislike />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};
